import { CustomButton } from '@ui-kit'
import { addToaster, WorkflowTaskIdentifierType } from '@app-store'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { NAMESPACES, WORKFLOW_STATUS } from '../../utils/constants'
import { isReadOnlyUser } from '../../utils/permission'
import { STRINGS } from '../../utils/strings'
import { useAppDispatch } from '../../utils/agent.store'
import { uniqueId } from 'lodash'
import {
  CallOutboundEntityLayoutTaskPropsType,
  CallOutboundEntityLayoutTaskRef,
  CallOutboundTaskRef,
  EntityLayoutTaskRef,
} from '../../types/workflow.types'
import CallOutboundTask from './CallOutboundTask'
import { EntityLayoutTask } from './EntityLayoutTask'

export const CallOutboundEntityLayoutTask = forwardRef<
  CallOutboundEntityLayoutTaskRef,
  CallOutboundEntityLayoutTaskPropsType
>(
  (
    {
      task,
      recordId,
      updateTaskResource,
      reFetch,
      tabIndexOffset,
      taskList,
      isDisabled = false,
      refetchCounter,
      uiIdentifierRef,
    },
    ref
  ) => {
    const dispatch = useAppDispatch()
    const callOutboundRef = useRef<CallOutboundTaskRef>(null)
    const entityLayoutRef = useRef<EntityLayoutTaskRef>(null)
    const tabIndexOffsetValue = tabIndexOffset ? tabIndexOffset + 2 : undefined

    const [isEditMode, setIsEditMode] = useState(false)

    const canEdit =
      !isDisabled && !isReadOnlyUser() && task?.state === WORKFLOW_STATUS.OPEN

    const subIsDisabled = !isEditMode || !canEdit

    const saveAll = async () => {
      // Run both validations in parallel. Each sub-component shows its own
      // inline field errors (red highlights) if required fields are missing.
      const [callOutboundValid, entityLayoutValid] = await Promise.all([
        callOutboundRef.current?.validate() ?? true,
        entityLayoutRef.current?.validate() ?? true,
      ])

      if (!callOutboundValid || !entityLayoutValid) {
        dispatch(
          addToaster({
            message: NAMESPACES.REQUIRED_TOAST,
            type: 'error',
            id: uniqueId(),
          })
        )
        return
      }

      await callOutboundRef.current?.save()
      await entityLayoutRef.current?.save()

      setIsEditMode(false)
    }

    useImperativeHandle(ref, () => ({
      save: saveAll,
    }))

    useEffect(() => {
      uiIdentifierRef.current = {
        ...uiIdentifierRef.current,
        [task?.id ?? '']: {
          updateCaseData: async () => {
            await saveAll()
            return true
          },
        },
      }
    }, [task?.id])

    // Reset to view mode whenever the task changes (e.g. workflow advances to a
    // different task instance).
    useEffect(() => {
      setIsEditMode(false)
    }, [task?.id])

    return (
      <div>
        <div className="flex justify-end p-2">
          {isEditMode ? (
            <CustomButton
              isFilled
              onClick={saveAll}
              tabIndex={tabIndexOffset}
              dataQaId={NAMESPACES.UPDATE_BUTTON_DATA_QA_ID}
            >
              {NAMESPACES.SAVE_BUTTON}
            </CustomButton>
          ) : (
            canEdit && (
              <CustomButton
                isFilled
                onClick={() => setIsEditMode(true)}
                tabIndex={tabIndexOffset}
              >
                {STRINGS.EDIT}
              </CustomButton>
            )
          )}
        </div>
        <CallOutboundTask
          ref={callOutboundRef}
          task={task}
          tabIndexOffset={tabIndexOffset}
          isDisabled={subIsDisabled}
          updateTaskResource={updateTaskResource}
          hideSaveButton
        />
        <EntityLayoutTask
          ref={entityLayoutRef}
          task={task}
          recordId={recordId}
          isTask
          updateTaskResource={updateTaskResource}
          refetch={reFetch}
          tabIndexOffset={tabIndexOffsetValue}
          taskList={taskList}
          isDisabled={subIsDisabled}
          refetchCounter={refetchCounter}
          hideSaveButton
          overrideLayoutMode={isEditMode ? 'edit' : 'view'}
          subResourceId={
            (
              task?.identifier as WorkflowTaskIdentifierType & {
                sub_resource_id?: string
              }
            )?.sub_resource_id
          }
        />
      </div>
    )
  }
)

CallOutboundEntityLayoutTask.displayName = 'CallOutboundEntityLayoutTask'

export default CallOutboundEntityLayoutTask
